import React from 'react';
import { BarStack } from '@vx/shape';
// import { Group } from '@vx/group';
import { AxisBottom } from '@vx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@vx/scale';
import { timeFormat } from 'd3-time-format';
import { withTooltip, Tooltip } from '@vx/tooltip';
import { LegendOrdinal } from '@vx/legend';
import { min, max } from 'd3-array';
import { uniq, range, reverse } from 'lodash';
import { nest } from 'd3-collection';

import randomcolor from 'randomcolor';

const seed = 21;
const colors = [...randomcolor({
   count: 3,
   hue: '#a44afe',
   seed: seed
}), ...randomcolor({
   count: 3,
   hue: '#3be2b0',
   seed: seed
}), ...randomcolor({
   count: 3,
   hue: '#a44afe',
   seed: seed
}), ...randomcolor({
   count: 3,
   hue: '#3be2b0',
   seed: seed
})]

const nestTime = data => nest()
  .key((c) => {
    const time = new Date(c.timestamp * 1000);
    return new Date(time.getFullYear(), time.getMonth(), time.getDate(), time.getHours());
  })
  .entries(data);

function SegmentationChart({
  width,
  height,
  events = false,
  margin = {
    top: 40
  },
  contract,
  contractData,
  filter,
  segmentBy,
  tooltipOpen,
  tooltipLeft,
  tooltipTop,
  tooltipData,
  hideTooltip,
  showTooltip
}) {
  if (width < 10) return null;
  if (!contractData) return null;

  const allData = events ? contractData.events : contractData.calls;

  const data = allData
    .filter(c => filter !== 'all' ? c.name === filter : true);

  let keys = (filter !== 'all' && segmentBy !== false)
    ? []
    : uniq(data.map(d => d.name));

  const preGroups = nestTime(data);
  const allPreGroups = nestTime(allData);

  const hours = allPreGroups.map(g => new Date(g.key).getTime());

  const rangeHours = reverse(reverse(range(min(hours), max(hours) + 60*60*1000, 60*60*1000)
  .map(h => (new Date(h)).toString())
  .map(h => {
    const group = preGroups.find(g => g.key === h);
    return {
      key: h,
      values: group ? group.values : []
    };
  })).splice(0,25));

  const groups = rangeHours.map(g => {
    if (filter !== 'all' && segmentBy !== false && !events) {
      const { index } = segmentBy;
      g.values = nest()
        .key(d => d.inputs[index])
        .entries(g.values);
      keys = uniq([...keys, ...g.values.map(v => v.key)]);
    } else if (filter !== 'all' && segmentBy !== false && events) {
      const { name } = segmentBy;
      g.values = nest()
        .key(d => d.args[name])
        .entries(g.values);
      keys = uniq([...keys, ...g.values.map(v => v.key)]);
    } else {
      g.values = nest()
        .key(d => d.name)
        .entries(g.values);
    }
    return g;
  });

  const groupsData = groups.map(g => {
    const values = {};
    g.values.forEach(v => values[v.key] = v.values.length);
    values._key = g.key;
    return values;
  });

  //_.range(new Date(2017, 10, 6, 4), new Date(2017, 10, 6, 10), 24*60*60*1000)

  // const parseDate = timeParse('%Y%m%d');
  const format = timeFormat('% %H:00');

  //['#ff5252', '#fb9999', '#6a52ff', '#92868e', '#52ffb5', '#96daf9', '#aa9cff', '#c7bfc5', '#f4ee33'];

  const genColors = (number) => {
    let totalColors = colors;
    while (totalColors.length < number) {
      totalColors = [...totalColors].concat(colors);
    }
    return totalColors.slice(0, number);
  }
  // accessors
  const x = (d, i) => d.key;

  const xGroupsData = d => d._key;
  // const y = d => d.value;

  const totals = groups.map(g => g.values.reduce((a,b) => (a+b.values.length),0));

  // bounds
  const xMax = width;
  const yMax = height - margin.top - 40;

  // // scales
  const xScale = scaleBand({
    rangeRound: [0, xMax],
    domain: groups.map(x),
    padding: 0.2,
    tickFormat: () => val => format(new Date(val))
  });
  const yScale = scaleLinear({
    rangeRound: [yMax, 0],
    nice: true,
    domain: [0, max(totals)*1.1]
  });
  const zScale = scaleOrdinal({
    domain: keys,
    range: genColors(groups.length)
  });

  let tooltipTimeout;

  return (
    <div style={{ position: 'relative' }}>
      <svg width={width} height={height}>
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill='#fff'
          rx={0}
        />
        <BarStack
          top={margin.top}
          data={groupsData}
          keys={keys}
          height={yMax}
          x={xGroupsData}
          xScale={xScale}
          yScale={yScale}
          zScale={zScale}
          onMouseLeave={data => event => {
            tooltipTimeout = setTimeout(() => {
              hideTooltip();
            }, 300);
          }}
          onMouseMove={data => event => {
            if (tooltipTimeout) clearTimeout(tooltipTimeout);
            const top = event.clientY - margin.top - 100;
            const left =
              xScale(data.x) + data.width + data.paddingInner * data.step / 2;
            showTooltip({
              tooltipData: data,
              tooltipTop: top,
              tooltipLeft: left
            });
          }}
        />
        <AxisBottom
          scale={xScale}
          top={yMax + margin.top}
          stroke="#a44afe"
          tickStroke="#a44afe"
          tickLabelProps={(value, index) => ({
            fill: '#676767',
            fontSize: 11,
            textAnchor: 'middle',
          })}
        />
      </svg>

      {
        keys.length < 10 ? (
          <div
            style={{
              position: 'absolute',
              top: margin.top / 2 - 10,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              fontSize: '14px'
            }}
          >
            <LegendOrdinal
              scale={zScale}
              direction="row"
              labelMargin="0 15px 0 0"
            />
          </div>
        ) : null
      }

      {tooltipOpen &&
        <Tooltip
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            minWidth: 60,
            backgroundColor: 'rgba(255,255,255,0.9)',
            color: '#676767'
          }}
        >
          <div style={{ color: zScale(tooltipData.key) }}>
            <strong>
              { tooltipData.key && tooltipData.key.length > 6
                ? tooltipData.key.slice(0,10) + '...'
                : tooltipData.key}
            </strong>
          </div>
          <div>
            Total: {tooltipData.data[tooltipData.key]}
          </div>
          <div>
            <small>
              {tooltipData.xFormatted}
            </small>
          </div>
        </Tooltip>}
    </div>
  );
}

/*
<BarStack
  onClick={data => event => {
    if (!events) return;
    alert(`clicked: ${JSON.stringify(data)}`);
  }}
*/

export default withTooltip(SegmentationChart);



// WEBPACK FOOTER //
// ./src/components/SegmentationChart.js